import { useState, useMemo } from 'react';
import './MiniCalendar.css';

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

interface ScheduledWord {
  date: string;
  word: string;
  id: string;
  difficulty?: string;
}

interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
}

interface MiniCalendarProps {
  scheduledWords?: ScheduledWord[];
  selectedDate?: string;
  onSelectDate?: (dateStr: string, existingWord?: ScheduledWord | null) => void;
  currentWord?: string;
}

export default function MiniCalendar({ scheduledWords, selectedDate, onSelectDate, currentWord }: MiniCalendarProps) {
  const [viewDate, setViewDate] = useState<Date>(() => {
    if (selectedDate) return new Date(selectedDate + 'T12:00:00');
    return new Date();
  });

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const calendarDays = useMemo((): CalendarDay[] => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPad = firstDay.getDay();
    const totalDays = lastDay.getDate();
    const days: CalendarDay[] = [];

    for (let i = 0; i < startPad; i++) {
      days.push({ date: new Date(year, month, -startPad + i + 1), isCurrentMonth: false });
    }
    for (let i = 1; i <= totalDays; i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    const remaining = 7 - (days.length % 7);
    if (remaining < 7) {
      for (let i = 1; i <= remaining; i++) {
        days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
      }
    }
    return days;
  }, [year, month]);

  const wordsByDate = useMemo((): Record<string, ScheduledWord[]> => {
    const map: Record<string, ScheduledWord[]> = {};
    for (const w of (scheduledWords || [])) {
      if (w.date) {
        if (!map[w.date]) map[w.date] = [];
        map[w.date].push(w);
      }
    }
    return map;
  }, [scheduledWords]);

  const gapStats = useMemo((): { scheduled: number; gaps: number } => {
    const today = new Date();
    let scheduled = 0;
    let gaps = 0;
    for (let i = 0; i < 30; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      const key = formatDate(d);
      const hasWord = wordsByDate[key]?.length > 0 || key === selectedDate;
      if (hasWord) scheduled++;
      else gaps++;
    }
    return { scheduled, gaps };
  }, [wordsByDate, selectedDate]);

  const todayStr = formatDate(new Date());
  const monthLabel = viewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const handleDayClick = (dateStr: string, dayWords: ScheduledWord[]) => {
    if (dayWords.length > 0) {
      const existing = dayWords[0];
      if (onSelectDate) onSelectDate(dateStr, existing);
    } else {
      if (onSelectDate) onSelectDate(dateStr, null);
    }
  };

  return (
    <div className="mini-cal">
      <div className="mini-cal-header">
        <button className="mini-cal-nav" onClick={() => setViewDate(new Date(year, month - 1, 1))}>&larr;</button>
        <span className="mini-cal-month">{monthLabel}</span>
        <button className="mini-cal-nav" onClick={() => setViewDate(new Date(year, month + 1, 1))}>&rarr;</button>
      </div>

      <div className="mini-cal-weekdays">
        {WEEKDAYS.map((d, i) => (
          <div key={i} className="mini-cal-wday">{d}</div>
        ))}
      </div>

      <div className="mini-cal-grid">
        {calendarDays.map((day, i) => {
          const dateStr = formatDate(day.date);
          const dayWords = wordsByDate[dateStr] || [];
          const isToday = dateStr === todayStr;
          const isSelected = dateStr === selectedDate;
          const hasWord = dayWords.length > 0;

          return (
            <button
              key={i}
              className={[
                'mini-cal-day',
                !day.isCurrentMonth && 'mini-cal-other',
                isToday && 'mini-cal-today',
                isSelected && 'mini-cal-selected',
                hasWord && !isSelected && 'mini-cal-occupied',
              ].filter(Boolean).join(' ')}
              onClick={() => handleDayClick(dateStr, dayWords)}
              title={hasWord ? dayWords.map((w) => w.word).join(', ') : 'Empty'}
            >
              <span className="mini-cal-num">{day.date.getDate()}</span>
              {hasWord && !isSelected && (
                <span className="mini-cal-dot" />
              )}
              {isSelected && (
                <span className="mini-cal-marker">{currentWord}</span>
              )}
            </button>
          );
        })}
      </div>

      <div className="mini-cal-stats">
        Next 30 days: <strong>{gapStats.scheduled}</strong> scheduled, <strong className={gapStats.gaps > 5 ? 'mini-cal-gap-warn' : ''}>{gapStats.gaps}</strong> gaps
      </div>
    </div>
  );
}

function formatDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
